import { TriangleAlert, Lightbulb, Calendar } from 'lucide-react';
import { format, parseISO } from 'date-fns';

import type { WeatherConflict } from '@/services/weather-task-service';

export interface WeatherConflictCardProps {
  conflict: WeatherConflict;
  onReschedule: (taskId: string, newDate: string) => void;
  onDismiss: (taskId: string) => void;
}

export function WeatherConflictCard({ conflict, onReschedule, onDismiss }: WeatherConflictCardProps) {
  const { taskId, taskTitle, dueDate, weatherConcern, suggestedDate, suggestedReason, severity } = conflict;

  const dueDateFormatted = format(parseISO(dueDate), 'EEE MMM d');
  const suggestedFormatted = suggestedDate ? format(parseISO(suggestedDate), 'MMM d') : null;

  const borderColor = severity === 'warning' ? 'border-amber-700/40' : 'border-yellow-700/30';
  const bgColor = severity === 'warning' ? 'bg-amber-900/20' : 'bg-yellow-900/10';

  return (
    <div
      className={`${bgColor} border ${borderColor} rounded-lg p-4 flex flex-col gap-3`}
      data-testid="weather-conflict-card"
    >
      {/* Header */}
      <div className="flex items-center gap-2">
        <TriangleAlert size={14} className="text-amber-400 flex-shrink-0" />
        <span className="field-label text-amber-400">
          Weather Alert
        </span>
      </div>

      {/* Concern + task */}
      <div className="flex flex-col gap-1">
        <p className="text-body-sm text-[var(--color-text-primary)]">
          <span className="text-amber-300">{weatherConcern}</span>
        </p>
        <p className="text-body-sm text-[var(--color-text-secondary)]">
          Task: <span className="font-medium text-[var(--color-text-primary)]">{taskTitle}</span>
        </p>
        <p className="flex items-center gap-1.5 text-body-xs text-[var(--color-text-placeholder)]">
          <Calendar size={11} />
          Due: {dueDateFormatted}
        </p>
      </div>

      {/* Suggestion */}
      {suggestedDate && suggestedFormatted && (
        <div className="flex items-start gap-2 px-3 py-2 rounded-md bg-bark-800/60 border border-bark-600/40">
          <Lightbulb size={13} className="text-[var(--color-text-muted)] flex-shrink-0 mt-0.5" />
          <p className="text-body-xs text-[var(--color-text-secondary)]">
            Move to <span className="font-medium text-[var(--color-text-primary)]">{suggestedFormatted}</span>?{' '}
            <span className="text-[var(--color-text-placeholder)]">{suggestedReason}</span>
          </p>
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-2 pt-1">
        {suggestedDate && (
          <button
            type="button"
            onClick={() => onReschedule(taskId, suggestedDate)}
            className="flex-1 px-3 py-1.5 rounded-md text-body-xs font-medium bg-[var(--color-accent-hover)] text-[var(--color-text-primary)] hover:bg-[var(--color-accent)] transition-colors"
            data-testid="reschedule-button"
          >
            Reschedule to {suggestedFormatted}
          </button>
        )}
        <button
          type="button"
          onClick={() => onDismiss(taskId)}
          className="px-3 py-1.5 rounded-md text-body-xs font-medium bg-[var(--color-elevated)] text-[var(--color-text-muted)] hover:bg-[var(--color-elevated)] hover:text-[var(--color-text-secondary)] transition-colors border border-[var(--color-border)]"
          data-testid="keep-date-button"
        >
          Keep Date
        </button>
        <button
          type="button"
          onClick={() => onDismiss(taskId)}
          className="px-3 py-1.5 rounded-md text-body-xs text-[var(--color-text-placeholder)] hover:text-[var(--color-text-muted)] transition-colors"
          data-testid="dismiss-button"
        >
          Dismiss
        </button>
      </div>
    </div>
  );
}
